import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CookingMethod, type MenuItemDto } from '@bhops/shared';
import { menuOfferingsApi } from '@/api/menu-offerings';
import { extractApiError } from '@/api/client';
import { Button } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { useToast } from '@/components/Toast';

type ExportFormat = 'csv' | 'xlsx';

interface Props {
  storeId: string;
  // round3 #8a — VIEWER 등 쓰기 권한이 없는 사용자에겐 편집 컨트롤을 비활성화.
  // (실제 차단은 백엔드 RBAC; 이건 UX 게이트.) 기본 true 로 기존 호출 보존.
  canEdit?: boolean;
}

const COOKING_METHODS = Object.values(CookingMethod) as CookingMethod[];

const COOKING_METHOD_LABELS: Record<CookingMethod, string> = {
  MAILLARD_GAS: '마이야르/가스 □',
  NEW_MAILLARD: '신규마이야르 ※',
  CAST_CHARCOAL: '주물판/숯 ■',
  DIRECT_GAS: '직화/가스 ▨',
  GRILL_CHARCOAL: '석쇠/숯 ▥',
  GRIDDLE_ELECTRIC: '그리들/전기 ◇',
  OVEN_PLUS: '오븐+ ▣',
};

function offeringKey(menuItemId: string, cookingMethod: CookingMethod): string {
  return `${menuItemId}:${cookingMethod}`;
}

interface DiffResult {
  added: string[];
  removed: string[];
  unchangedCount: number;
}

function computeDiff(current: Set<string>, original: Set<string>): DiffResult {
  const added: string[] = [];
  const removed: string[] = [];
  for (const key of current) if (!original.has(key)) added.push(key);
  for (const key of original) if (!current.has(key)) removed.push(key);
  const unchangedCount = current.size - added.length;
  return { added, removed, unchangedCount };
}

export function MenuOfferingTab({ storeId, canEdit = true }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [original, setOriginal] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close the export-menu when clicking outside.
  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['menu-offerings', storeId],
    queryFn: () => menuOfferingsApi.getByStore(storeId),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!data) return;
    const initial = new Set(data.offerings.map((o) => offeringKey(o.menuItemId, o.cookingMethod)));
    setChecked(initial);
    setOriginal(initial);
    setDirty(false);
  }, [data]);

  const menuItems: MenuItemDto[] = useMemo(() => data?.menuItems ?? [], [data]);

  // Map menuItemId → displayName for diff labels (memoized).
  const menuItemLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of menuItems) map.set(item.id, item.displayName);
    return map;
  }, [menuItems]);

  const diff = useMemo(() => computeDiff(checked, original), [checked, original]);

  const save = useMutation({
    mutationFn: () => {
      // Client-side validation: ensure every entry has a valid CookingMethod enum value.
      // Defense-in-depth: type system already enforces this, but if Set keys were ever
      // corrupted (e.g. via DevTools or future bulk-import), surface a friendly error
      // rather than letting the server reject silently.
      const validMethods = new Set<string>(COOKING_METHODS);
      for (const key of checked) {
        const [, method] = key.split(':');
        if (!validMethods.has(method)) {
          throw new Error(`잘못된 조리법 값: ${method}`);
        }
      }
      return menuOfferingsApi.upsertMatrix(storeId, {
        entries: Array.from(checked).map((key) => {
          const [menuItemId, cookingMethod] = key.split(':');
          return { menuItemId, cookingMethod: cookingMethod as CookingMethod };
        }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['menu-offerings', storeId] });
      toast.success('메뉴 구성을 저장했습니다.');
      setDirty(false);
      setPreviewOpen(false);
    },
    onError: (err) => toast.error(extractApiError(err), '저장 실패'),
  });

  function toggle(menuItemId: string, method: CookingMethod) {
    const key = offeringKey(menuItemId, method);
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setDirty(true);
  }

  function toggleColumn(method: CookingMethod, target: 'all' | 'none') {
    setChecked((prev) => {
      const next = new Set(prev);
      for (const item of menuItems) {
        const key = offeringKey(item.id, method);
        if (target === 'all') next.add(key);
        else next.delete(key);
      }
      return next;
    });
    setDirty(true);
  }

  async function handleExport(format: ExportFormat) {
    setMenuOpen(false);
    setExporting(true);
    try {
      if (format === 'csv') {
        await menuOfferingsApi.exportCsv(storeId);
      } else {
        await menuOfferingsApi.exportXlsx(storeId);
      }
    } catch (err) {
      const label = format === 'csv' ? 'CSV 내보내기 실패' : 'Excel 내보내기 실패';
      toast.error(extractApiError(err), label);
    } finally {
      setExporting(false);
    }
  }

  if (isLoading) return <div style={loadingStyle}>불러오는 중…</div>;
  if (error) return <div style={errorStyle}>오류: {extractApiError(error)}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 className="kuun-h3" style={{ margin: 0 }}>
          메뉴 구성
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <div ref={menuRef} style={{ position: 'relative' }}>
            <Button variant="secondary" disabled={exporting} onClick={() => setMenuOpen((v) => !v)}>
              {exporting ? (
                '내보내는 중...'
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  내보내기
                  <Icon name="chev-down" size={14} aria-hidden />
                </span>
              )}
            </Button>
            {menuOpen && !exporting && (
              <div role="menu" style={menuStyle}>
                <button
                  type="button"
                  role="menuitem"
                  style={menuItemStyle}
                  onClick={() => handleExport('csv')}
                >
                  CSV (.csv)
                </button>
                <button
                  type="button"
                  role="menuitem"
                  style={menuItemStyle}
                  onClick={() => handleExport('xlsx')}
                >
                  Excel (.xlsx)
                </button>
              </div>
            )}
          </div>
          <Button
            variant="secondary"
            disabled={!canEdit || !dirty || save.isPending}
            onClick={() => setPreviewOpen(true)}
          >
            변경 미리보기 ({diff.added.length + diff.removed.length})
          </Button>
          <Button
            variant="primary"
            disabled={!canEdit || !dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? '저장 중...' : '저장'}
          </Button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...thStyle, minWidth: 120, textAlign: 'left' }}>메뉴</th>
              <th style={{ ...thStyle, minWidth: 60, fontSize: 11 }}>세그먼트</th>
              {COOKING_METHODS.map((m) => (
                <th key={m} style={{ ...thStyle, minWidth: 90, fontSize: 11 }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span>{COOKING_METHOD_LABELS[m]}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        type="button"
                        title={`${COOKING_METHOD_LABELS[m]} 열 전체 선택`}
                        disabled={!canEdit}
                        onClick={() => toggleColumn(m, 'all')}
                        style={columnButtonStyle}
                      >
                        전체
                      </button>
                      <button
                        type="button"
                        title={`${COOKING_METHOD_LABELS[m]} 열 전체 해제`}
                        disabled={!canEdit}
                        onClick={() => toggleColumn(m, 'none')}
                        style={columnButtonStyle}
                      >
                        해제
                      </button>
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {menuItems.map((item, idx) => (
              <tr
                key={item.id}
                style={{
                  background: idx % 2 === 0 ? 'var(--bg-surface)' : 'var(--bg-sunken)',
                }}
              >
                <td style={tdStyle}>{item.displayName}</td>
                <td style={{ ...tdStyle, color: 'var(--fg-muted)', fontSize: 11 }}>
                  {item.segment}
                </td>
                {COOKING_METHODS.map((method) => {
                  const key = offeringKey(item.id, method);
                  const isChecked = checked.has(key);
                  const wasChecked = original.has(key);
                  const isCellDirty = isChecked !== wasChecked;
                  const tooltip = isCellDirty
                    ? wasChecked
                      ? '원래 있던 칸을 뺐습니다'
                      : '원래 없던 칸을 넣었습니다'
                    : undefined;
                  return (
                    <td
                      key={method}
                      title={tooltip}
                      style={{
                        ...tdStyle,
                        textAlign: 'center',
                        ...(isCellDirty ? dirtyCellStyle : null),
                      }}
                      data-testid={isCellDirty ? `dirty-cell-${item.id}-${method}` : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={!canEdit}
                        onChange={() => toggle(item.id, method)}
                        style={{
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          width: 16,
                          height: 16,
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dirty && (
        <div style={{ color: 'var(--fg-muted)', fontSize: 12 }}>
          * 변경사항이 있습니다. 저장 버튼을 눌러 반영하세요.
        </div>
      )}

      {previewOpen && (
        <PreviewModal
          diff={diff}
          menuItemLabels={menuItemLabels}
          onCancel={() => setPreviewOpen(false)}
          onConfirm={() => save.mutate()}
          saving={save.isPending}
        />
      )}
    </div>
  );
}

interface PreviewModalProps {
  diff: DiffResult;
  menuItemLabels: Map<string, string>;
  onCancel: () => void;
  onConfirm: () => void;
  saving: boolean;
}

function PreviewModal({ diff, menuItemLabels, onCancel, onConfirm, saving }: PreviewModalProps) {
  function formatEntry(key: string): string {
    const [menuItemId, method] = key.split(':');
    const itemName = menuItemLabels.get(menuItemId) ?? menuItemId;
    const methodLabel = COOKING_METHOD_LABELS[method as CookingMethod] ?? method;
    return `${itemName} × ${methodLabel}`;
  }

  return (
    <div role="dialog" aria-labelledby="preview-modal-title" style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <h3 id="preview-modal-title" style={{ margin: 0, marginBottom: 12 }}>
          변경사항 미리보기
        </h3>

        <div style={diffSectionStyle}>
          <h4 style={{ margin: 0, marginBottom: 6, color: 'var(--robot-operating)' }}>
            추가 ({diff.added.length})
          </h4>
          {diff.added.length === 0 ? (
            <div style={emptyStyle}>없음</div>
          ) : (
            <ul style={listStyle}>
              {diff.added.map((key) => (
                <li key={key}>{formatEntry(key)}</li>
              ))}
            </ul>
          )}
        </div>

        <div style={diffSectionStyle}>
          <h4 style={{ margin: 0, marginBottom: 6, color: 'var(--fg-danger)' }}>
            제거 ({diff.removed.length})
          </h4>
          {diff.removed.length === 0 ? (
            <div style={emptyStyle}>없음</div>
          ) : (
            <ul style={listStyle}>
              {diff.removed.map((key) => (
                <li key={key}>{formatEntry(key)}</li>
              ))}
            </ul>
          )}
        </div>

        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 16 }}>
          유지:{' '}
          <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
            {diff.unchangedCount}
          </span>{' '}
          cells
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            취소
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const loadingStyle: React.CSSProperties = { padding: 24, color: 'var(--fg-muted)' };
const errorStyle: React.CSSProperties = { padding: 24, color: 'var(--fg-danger)' };

const tableStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-sunken)',
  fontWeight: 600,
  whiteSpace: 'pre-line',
};

const tdStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--border-default)',
};

const dirtyCellStyle: React.CSSProperties = {
  outline: '2px solid var(--border-focus)',
  outlineOffset: '-2px',
  background: 'var(--bg-info-subtle)',
};

const menuStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  right: 0,
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 140,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  boxShadow: 'var(--shadow-md)',
  padding: 4,
};

const menuItemStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  textAlign: 'left',
  padding: '6px 10px',
  cursor: 'pointer',
  fontSize: 13,
  color: 'inherit',
  borderRadius: 'var(--r-input)',
};

const columnButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  padding: '1px 4px',
  fontSize: 10,
  cursor: 'pointer',
  color: 'var(--fg-muted)',
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'color-mix(in srgb, var(--ink-bg) 45%, transparent)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle: React.CSSProperties = {
  background: 'var(--bg-surface)',
  borderRadius: 'var(--r-modal)',
  padding: 24,
  width: 'min(520px, 90vw)',
  maxHeight: '80vh',
  overflowY: 'auto',
  boxShadow: 'var(--shadow-lg)',
};

const diffSectionStyle: React.CSSProperties = {
  marginBottom: 12,
};

const listStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  fontSize: 13,
  lineHeight: '20px',
};

const emptyStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-subtle)',
  fontStyle: 'italic',
};
